import "./imageInfo.css";

import { apm } from "@elastic/apm-rum";
import { Close } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import UseAuth from "shared/common/hooks/UseAuth";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { coordSystems } from "shared/common/utils/constants/coordSystems";
import { addThousandsSeparator } from "shared/common/utils/general-utils";

type Info = {
  title: string;
  description: string;
};

export default function ImageInfoContent() {
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);

  const closeAllToolPopover = sidebarStore(
    (state) => state.closeAllToolPopover
  );
  const selectedTableRows = sidebarStore((state) => state.selectedTableRows);

  const clientConfig = defaultStore((state) => state.clientConfig);

  const [infos, setInfos] = useState<Info[]>([]);

  const { t, i18n } = useTranslation();

  const { userData } = UseAuth();

  //close image popover
  const handleCloseImage = () => {
    closeAllToolPopover();
  };

  //when node and camera configuration change, update image info datas
  useEffect(() => {
    if (viewer !== undefined) {
      viewer.on("cameraconfigurationchanged", () => {
        updateInfos();
      });
      viewer.on("geoframechanged", () => {
        updateInfos();
      });
      viewer.on("navigationaxischanged", () => {
        updateInfos();
      });
    }
  }, [viewer]);

  useEffect(() => {
    updateInfos();
  }, [i18n.language]);

  const updateInfos = () => {
    const geoframe = viewer?.getScene().getCurrentGeoframe();
    if (!geoframe) {
      setInfos([]);
      return;
    }

    const info: Info[] = [
      {
        title: t("image_info.date"),
        description: new Date(geoframe.timestamp).toLocaleString("de-CH"),
      },
      {
        title: t("image_info.position"),
        description:
          addThousandsSeparator(geoframe.easting.toFixed(2)) +
          ", " +
          addThousandsSeparator(geoframe.northing.toFixed(2)),
      },
      {
        title: t("image_info.height"),
        description: addThousandsSeparator(geoframe.height.toFixed(0)) + " m",
      },
      {
        title: t("image_info.crs"),
        description: coordSystems[geoframe.srid]
          ? coordSystems[geoframe.srid]
          : "EPSG: " + geoframe.srid,
      },
      {
        title: t("image_info.type"),
        description: geoframe.projection,
      },
      {
        title: t("image_info.campaign"),
        description: geoframe.campaign.description,
      },
    ];

    if (
      userData?.scope?.includes("addon/linref") &&
      viewer?.getScene().getLRHandler().getActiveNavigationAxis()
    ) {
      const axis = viewer.getScene().getLRHandler().getActiveNavigationAxis();
      const uvw = axis?.xyz2uvw(
        geoframe.easting,
        geoframe.northing,
        geoframe.height
      );
      if (axis && uvw) {
        info.splice(4, 0, {
          title: t("linref.axis"),
          description: axis.name,
        });

        if (clientConfig?.linref?.ref_point_attribute) {
          const axisid_obj = axis.getAxisIdentifier(uvw.featureidx);
          const axisid = Object.keys(axisid_obj)
            .map((x) => {
              return axisid_obj[x];
            })
            .slice(-1);
          info.splice(4, 0, {
            title: t("linref.ref_point"),
            description: axisid.toString(),
          });
        }

        info.splice(5, 0, {
          title: t("image_info.along_axis"),
          description:
            addThousandsSeparator(uvw?.lrcoords[0].toFixed(2)) + " m",
        });
      }
    }
    setInfos(info);
  };

  return (
    <Box
      height={selectedTableRows ? "40%" : "100%"}
      display="flex"
      flexDirection={"column"}
      sx={{
        transition: "height 0.3s",
      }}
    >
      <Box
        height={"100%"}
        display={"flex"}
        flexDirection="column"
        gap={selectedTableRows ? 1 : 2}
        paddingX={1.5}
        paddingY={1.5}
        sx={{
          overflow: "hidden",
          backgroundColor: darkTheme ? "darkblue.main" : "white",
          borderRadius: "10px",
          transition: "height 0.3s , margin 0.3s , padding 0.3s",
          marginBottom: selectedTableRows ? 1 : 0,
        }}
      >
        <Box
          display={"flex"}
          flexDirection="row"
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ color: darkTheme ? "white" : "darkblue.main" }}
        >
          <Typography
            fontWeight={"600"}
            noWrap
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
            }}
          >
            {t("image_info.title")}
          </Typography>
          <IconButton
            sx={{
              padding: 0,
              marginY: 1,
              color: darkTheme ? "white" : "darkblue.main",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
            onClick={() =>
              monitor(apm, "close-image-info | Image Info", handleCloseImage)
            }
            disableRipple={true}
          >
            <Close />
          </IconButton>
        </Box>
        <Box
          width="100%"
          sx={{
            display: "block",
            height: "100%",
            overflow: "auto",
          }}
        >
          {infos.length > 0 && (
            <Table
              sx={{
                backgroundColor: darkTheme ? "darkblue.main" : "white",
              }}
            >
              <TableBody>
                {infos.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell
                      width={"40%"}
                      align="left"
                      sx={{
                        padding: "4px 0px",
                        border: "none",
                        verticalAlign: "top",
                      }}
                    >
                      <Typography
                        color={darkTheme ? "white" : "darkblue.main"}
                        fontSize={"14px"}
                        fontWeight={600}
                      >
                        {item.title}:
                      </Typography>
                    </TableCell>
                    <TableCell
                      width={"60%"}
                      align="left"
                      sx={{
                        padding: "4px 0px",
                        border: "none",
                        verticalAlign: "top",
                      }}
                    >
                      <Typography
                        color={darkTheme ? "white" : "darkblue.main"}
                        fontSize={"14px"}
                      >
                        {item.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {infos.length === 0 && skeleton()}
        </Box>
      </Box>
    </Box>
  );
}

export const skeleton = () => {
  return (
    <Box display={"flex"} flexDirection={"column"} gap={1} paddingY={1}>
      {[...Array(5)].map((item, index) => (
        <Box key={index} display={"flex"} flexDirection={"row"} gap={1}>
          <Skeleton variant="rectangular" width={"40%"} height="16px" />
          <Skeleton variant="rectangular" width={"60%"} height="16px" />
        </Box>
      ))}
    </Box>
  );
};
